/**
 * Выдуманная книга «для мамы» для превью кабинета (/preview/cabinet), на
 * каждом языке ролика своя. Модуль без импортов: его читает и браузерная
 * заглушка API, и скрипт, который рисует PDF-страницы настоящим генератором
 * бэкенда.
 */

/**
 * Локаль ролика — рынок лендинга: язык кабинета плюс свой набор текстов.
 * ru — русский для Казахстана (Абай, баурсаки, Алматы), ru-ru — русский для
 * России и Беларуси, tr — Турция, en — Запад.
 */
export type PreviewLang = 'ru' | 'ru-ru' | 'tr' | 'en';
export const PREVIEW_LANGS: PreviewLang[] = ['ru', 'ru-ru', 'tr', 'en'];

export const isPreviewLang = (v: string | null | undefined): v is PreviewLang =>
  !!v && (PREVIEW_LANGS as string[]).includes(v);

/**
 * Локаль ролика под язык страницы лендинга (там, где ролик идёт за языком):
 * турецкий и английский — свои, все остальные языки — русский казахстанский.
 */
export const previewLangForPage = (pageLang: string | null | undefined): PreviewLang => {
  const l = (pageLang || '').toLowerCase().split('-')[0];
  return l === 'tr' || l === 'en' ? l : 'ru';
};

interface Market {
  /** Код языка интерфейса кабинета (i18n/languages.ts). */
  ui: string;
  /** Язык книги, как его хранит бэкенд (от него зависит буквица). */
  book: string;
  city: string;
  country: string;
  /** Подсказка в поле даты фото. */
  datePlaceholder: string;
}

export const MARKETS: Record<PreviewLang, Market> = {
  ru: { ui: 'ru', book: 'ru', city: 'Алматы', country: 'KZ', datePlaceholder: 'дд.мм.гггг' },
  'ru-ru': { ui: 'ru', book: 'ru', city: 'Москва', country: 'RU', datePlaceholder: 'дд.мм.гггг' },
  tr: { ui: 'tr', book: 'tr', city: 'İstanbul', country: 'TR', datePlaceholder: 'gg.aa.yyyy' },
  en: { ui: 'en', book: 'en', city: 'New York', country: 'US', datePlaceholder: 'dd.mm.yyyy' },
};

interface Content {
  questions: string[];
  chapters: [string, string, string];
  /** Ответы: номер вопроса → [текст, страниц в книге]. */
  answers: Record<number, [string, number]>;
  photos: [string, string, string];
  author: string;
  receiver: string;
  title: string;
  script: {
    answer: string;
    photoDescription: string;
    bookTitle: string;
  };
}

const CONTENT: Record<PreviewLang, Content> = {
  ru: {
    questions: [
      'Каким было твоё детство? Что первым приходит на ум?',
      'Кем ты мечтала стать, когда была маленькой?',
      'Какое блюдо из детства ты до сих пор помнишь на вкус?',
      'Расскажи о своих родителях. Чему они тебя научили?',
      'Какой была твоя первая работа?',
      'Как вы познакомились с папой?',
      'Каким был день, когда я родилась?',
      'Что было самым трудным в первые годы материнства?',
      'Какую семейную традицию ты больше всего любишь?',
      'Какое путешествие ты вспоминаешь чаще других?',
      'Каким ты видишь себя через десять лет?',
      'Что ты хотела бы сказать себе двадцатилетней?',
      'За что ты благодарна жизни?',
      'Какой совет ты дала бы своим внукам?',
      'Что для тебя значит дом?',
    ],
    chapters: ['Детство', 'Наша семья', 'Что дальше'],
    answers: {
      1: [
        'Детство — это двор на Абая, где все друг друга знали. Летом мы с девчонками до темноты играли в классики, а бабушка звала ужинать с балкона на весь двор. Зимой отец заливал горку, и мы катались на картонках, пока не промокали насквозь. Дома всегда пахло хлебом: мама пекла по воскресеньям, и мне доверяли смазывать корочку маслом.',
        2,
      ],
      2: [
        'Учительницей. Я рассаживала кукол на диване, ставила им оценки в старую тетрадь и строго проверяла домашние задания. Потом захотела быть врачом, как тётя Роза. А в итоге стала бухгалтером и ни разу об этом не пожалела: цифры меня всегда успокаивали.',
        1,
      ],
      3: [
        'Бабушкины баурсаки. Она жарила их в большом казане, и мы таскали их прямо из миски, обжигая пальцы. Я много раз пыталась повторить, но таких пышных у меня так и не получилось. Наверное, дело было не в рецепте.',
        1,
      ],
      4: [
        'Папа был строгим, но справедливым. Он учил, что слово нужно держать, даже когда это неудобно. Мама научила меня терпению и тому, что любой спор можно решить за чаем. Я до сих пор так и делаю.',
        1,
      ],
      6: [
        'На свадьбе общих друзей. Твой папа весь вечер не решался пригласить меня на танец, а когда наконец подошёл, музыка закончилась. Он так и стоял с протянутой рукой, и я рассмеялась. Через год мы поженились.',
        2,
      ],
      7: [
        'Был март, шёл мокрый снег. Папа три часа ходил под окнами роддома с букетом тюльпанов, которые где-то достал. Когда тебя принесли, ты не плакала, а очень серьёзно на меня смотрела. Я тогда подумала: эта девочка всё про меня поняла.',
        2,
      ],
    },
    photos: ['Мама и её любимое кресло', 'Озеро за дачей', 'Наша первая весна'],
    author: 'Анна',
    receiver: 'Маме',
    title: 'Мамина книга',
    script: {
      answer: 'В девятнадцать лет я пошла работать кассиром. Первую зарплату потратила на подарки родителям.',
      photoDescription: 'Первый рабочий день',
      bookTitle: 'Мама, это про тебя',
    },
  },
  tr: {
    questions: [
      'Çocukluğun nasıldı? Aklına ilk ne geliyor?',
      'Küçükken ne olmak isterdin?',
      'Çocukluğundan hâlâ tadını hatırladığın yemek hangisi?',
      'Annen ve baban nasıl insanlardı? Sana neler öğrettiler?',
      'İlk işin neydi?',
      'Babamla nasıl tanıştınız?',
      'Ben doğduğum gün nasıldı?',
      'Anneliğin ilk yıllarında en zor olan neydi?',
      'En sevdiğin aile geleneği hangisi?',
      'En çok hatırladığın yolculuk hangisi?',
      'On yıl sonra kendini nerede görüyorsun?',
      'Yirmi yaşındaki haline ne söylemek isterdin?',
      'Hayatta en çok neye minnettarsın?',
      'Torunlarına ne tavsiye ederdin?',
      'Senin için ev ne demek?',
    ],
    chapters: ['Çocukluk', 'Ailemiz', 'Bundan sonrası'],
    answers: {
      1: [
        'Çocukluğum, herkesin birbirini tanıdığı bir mahallede geçti. Yazları hava kararana kadar sokakta seksek oynardık, babaannem de balkondan bütün mahalleye duyurarak bizi yemeğe çağırırdı. Kışın babam bahçede küçük bir buz pisti yapardı. Evde hep taze ekmek kokusu olurdu: annem pazar günleri ekmek pişirir, üstüne tereyağı sürmeyi bana bırakırdı.',
        2,
      ],
      2: [
        'Öğretmen olmak isterdim. Bebeklerimi kanepeye dizer, eski bir deftere notlarını yazar, ödevlerini ciddi ciddi kontrol ederdim. Sonra teyzem gibi doktor olmak istedim. Sonunda muhasebeci oldum ve hiç pişman olmadım: rakamlar beni hep sakinleştirdi.',
        1,
      ],
      3: [
        'Babaannemin pişileri. Kocaman bir tavada kızartırdı, biz de daha soğumadan parmaklarımızı yakarak kaseden kapardık. Defalarca denedim ama hiçbiri onunkiler kadar kabarık olmadı. Galiba mesele tarifte değildi.',
        1,
      ],
      4: [
        'Babam sert ama adil biriydi. Verilen sözün, işine gelmese bile tutulması gerektiğini öğretti. Annem bana sabrı ve her tartışmanın bir bardak çayla çözülebileceğini öğretti. Ben hâlâ öyle yapıyorum.',
        1,
      ],
      6: [
        'Ortak arkadaşlarımızın düğününde. Baban bütün akşam beni dansa kaldırmaya cesaret edemedi, tam yanıma geldiğinde de müzik bitti. Elini uzatmış öylece kalakaldı, ben de gülmeye başladım. Bir yıl sonra evlendik.',
        2,
      ],
      7: [
        'Mart ayıydı, sulu kar yağıyordu. Baban bir yerden bulduğu lalelerle hastanenin penceresinin altında üç saat volta attı. Seni getirdiklerinde ağlamıyordun, bana çok ciddi bakıyordun. O an düşündüm: bu kız beni anladı.',
        2,
      ],
    },
    photos: ['Annem ve sevdiği koltuk', 'Yazlığın arkasındaki göl', 'İlk baharımız'],
    author: 'Ayşe',
    receiver: 'Anneme',
    title: 'Annemin kitabı',
    script: {
      answer: 'On dokuz yaşımda kasiyer olarak işe başladım. İlk maaşımla aileme hediyeler aldım.',
      photoDescription: 'İşteki ilk günüm',
      bookTitle: 'Anneciğim, bu senin hikâyen',
    },
  },
  'ru-ru': {
    questions: [
      'Каким было твоё детство? Что первым приходит на ум?',
      'Кем ты мечтала стать, когда была маленькой?',
      'Какое блюдо из детства ты до сих пор помнишь на вкус?',
      'Расскажи о своих родителях. Чему они тебя научили?',
      'Какой была твоя первая работа?',
      'Как вы познакомились с папой?',
      'Каким был день, когда я родилась?',
      'Что было самым трудным в первые годы материнства?',
      'Какую семейную традицию ты больше всего любишь?',
      'Какое путешествие ты вспоминаешь чаще других?',
      'Каким ты видишь себя через десять лет?',
      'Что ты хотела бы сказать себе двадцатилетней?',
      'За что ты благодарна жизни?',
      'Какой совет ты дала бы своим внукам?',
      'Что для тебя значит дом?',
    ],
    chapters: ['Детство', 'Наша семья', 'Что дальше'],
    answers: {
      1: [
        'Детство — это двор на Садовой, где все друг друга знали. Летом мы с девчонками до темноты играли в классики, а бабушка звала ужинать с балкона на весь двор. Зимой отец заливал горку, и мы катались на картонках, пока не промокали насквозь. Дома всегда пахло хлебом: мама пекла по воскресеньям, и мне доверяли смазывать корочку маслом.',
        2,
      ],
      2: [
        'Учительницей. Я рассаживала кукол на диване, ставила им оценки в старую тетрадь и строго проверяла домашние задания. Потом захотела быть врачом, как тётя Валя. А в итоге стала бухгалтером и ни разу об этом не пожалела: цифры меня всегда успокаивали.',
        1,
      ],
      3: [
        'Бабушкины пирожки с капустой. Она пекла их целыми противнями, и мы таскали их прямо из миски, обжигая пальцы. Я много раз пыталась повторить, но таких пышных у меня так и не получилось. Наверное, дело было не в рецепте.',
        1,
      ],
      4: [
        'Папа был строгим, но справедливым. Он учил, что слово нужно держать, даже когда это неудобно. Мама научила меня терпению и тому, что любой спор можно решить за чаем. Я до сих пор так и делаю.',
        1,
      ],
      6: [
        'На свадьбе общих друзей. Твой папа весь вечер не решался пригласить меня на танец, а когда наконец подошёл, музыка закончилась. Он так и стоял с протянутой рукой, и я рассмеялась. Через год мы поженились.',
        2,
      ],
      7: [
        'Был март, шёл мокрый снег. Папа три часа ходил под окнами роддома с букетом тюльпанов, которые где-то достал. Когда тебя принесли, ты не плакала, а очень серьёзно на меня смотрела. Я тогда подумала: эта девочка всё про меня поняла.',
        2,
      ],
    },
    photos: ['Мама и её любимое кресло', 'Озеро за дачей', 'Наша первая весна'],
    author: 'Анна',
    receiver: 'Маме',
    title: 'Мамина книга',
    script: {
      answer: 'В девятнадцать лет я пошла работать кассиром. Первую зарплату потратила на подарки родителям.',
      photoDescription: 'Первый рабочий день',
      bookTitle: 'Мама, это про тебя',
    },
  },
  en: {
    questions: [
      'What was your childhood like? What comes to mind first?',
      'What did you dream of becoming when you were little?',
      'Which dish from your childhood can you still taste?',
      'Tell me about your parents. What did they teach you?',
      'What was your first job?',
      'How did you and Dad meet?',
      'What was the day I was born like?',
      'What was hardest in your first years as a mother?',
      'Which family tradition do you love most?',
      'Which trip do you remember most often?',
      'Where do you see yourself in ten years?',
      'What would you tell your twenty-year-old self?',
      'What are you most grateful for in life?',
      'What advice would you give your grandchildren?',
      'What does home mean to you?',
    ],
    chapters: ['Childhood', 'Our family', 'What comes next'],
    answers: {
      1: [
        'My childhood was a street where everyone knew each other. In summer we played hopscotch until dark, and Grandma called us in for dinner from the porch so the whole street could hear. In winter Dad built us a little sledding hill in the yard. The house always smelled of bread: Mom baked every Sunday and let me brush the crust with butter.',
        2,
      ],
      2: [
        'A teacher. I sat my dolls on the sofa, graded them in an old notebook and checked their homework very strictly. Later I wanted to be a doctor like Aunt Rose. In the end I became an accountant and never regretted it: numbers always calmed me down.',
        1,
      ],
      3: [
        "Grandma's apple pie. She baked it in a huge dish, and we sneaked slices before it had cooled, burning our fingers. I've tried to make it many times, but mine never turned out the same. Maybe it was never about the recipe.",
        1,
      ],
      4: [
        'Dad was strict but fair. He taught me to keep my word, even when it was inconvenient. Mom taught me patience, and that any argument can be settled over a cup of tea. I still do exactly that.',
        1,
      ],
      6: [
        "At a friends' wedding. Your dad spent the whole evening working up the courage to ask me to dance, and when he finally came over, the music stopped. He just stood there with his hand out, and I burst out laughing. A year later we were married.",
        2,
      ],
      7: [
        "It was March, and wet snow was falling. Dad paced under the hospital windows for three hours with a bunch of tulips he had found somewhere. When they brought you to me, you didn't cry, you just looked at me very seriously. I thought: this girl has me all figured out.",
        2,
      ],
    },
    photos: ['Mom and her favorite armchair', 'The lake behind the cabin', 'Our first spring'],
    author: 'Emily',
    receiver: 'For Mom',
    title: "Mom's book",
    script: {
      answer: 'At nineteen I got a job as a cashier. I spent my first paycheck on gifts for my parents.',
      photoDescription: 'My first day at work',
      bookTitle: 'Mom, this is your story',
    },
  },
};

/** Что посетитель «делает» в ролике на языке lang. */
export function scriptFor(lang: PreviewLang) {
  return {
    questionId: 5,
    ...CONTENT[lang].script,
    photo: 'landing/newpeople/бабушке.jpg',
    /** Печатается в поле даты как текст (дд.мм.гггг одинаково для всех локалей). */
    photoDate: '01.09.1987',
    coverId: 'cover-3',
    platePreset: 'center',
  };
}

export function dataFor(lang: PreviewLang) {
  const c = CONTENT[lang];
  const q = c.questions;
  const answer = (qid: number, [text, pageCount]: [string, number]) => ({
    id: qid,
    questionId: qid,
    text,
    preset: 'heading',
    clientQuestion: '',
    dropCap: false,
    pageCount,
    questionText: q[qid - 1]!,
    createdAt: '2026-10-25T10:00:00Z',
    updatedAt: '2026-10-25T10:00:00Z',
  });
  const photo = (id: number, file: string, description: string, date: string, questionTxt: string) => ({
    id,
    userId: 1,
    templateId: 1,
    questionTxt,
    url: file,
    description,
    position: 'fullbleed::top:sm::bottom:md::light',
    showDate: true,
    showDescription: true,
    date,
    createdAt: '2026-10-25T10:00:00Z',
  });
  return {
    template: { id: 1, name: c.title, language: MARKETS[lang].book, recipient: 'mom', createdAt: '2026-10-20' },
    questions: q.map((text, i) => ({ id: i + 1, templateId: 1, text, sortOrder: i + 1 })),
    chapters: c.chapters.map((name, i) => ({
      id: i + 1,
      name,
      templateName: name,
      fromSort: i * 5 + 1,
      toSort: i * 5 + 5,
      sortOrder: i,
    })),
    answers: Object.fromEntries(
      Object.entries(c.answers).map(([qid, a]) => [Number(qid), answer(Number(qid), a)]),
    ) as Record<number, ReturnType<typeof answer>>,
    photos: [
      photo(1, 'landing/newpeople/маме.jpg', c.photos[0], '2019-08-14', q[0]!),
      photo(2, 'onbpics/1.jpg', c.photos[1], '1998-07-15', q[0]!),
      photo(3, 'landing/newpeople/жене.jpg', c.photos[2], '1995-04-02', q[5]!),
    ],
    cover: {
      id: 1,
      userId: 1,
      templateId: 1,
      authorName: c.author,
      receiverName: c.receiver,
      bookTitle: c.title,
      coverUrl: 'cover-1',
      platePreset: 'frame',
    },
  };
}
